import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Camera, ImageSquare, Sparkle, ArrowRight, X, Key, Warning } from "@phosphor-icons/react";

const SLOTS = [
  { key: "fronte", label: "Fronte", required: true },
  { key: "retro", label: "Retro", required: false },
  { key: "lato", label: "Lato", required: false },
  { key: "lato2", label: "Lato 2", required: false },
];

const PhotoSlot = ({ slot, file, preview, onPick, onClear, photoInputMode }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onPick(f);
    e.target.value = "";
  };
  // Tap-to-open behaviour: in single-mode (file OR camera) the entire empty
  // slot acts as a big tap target; in "both" mode there's no single default
  // action, so the slot itself is NOT clickable and the user picks via the
  // two explicit chips. This fixes the bug where tapping "Scatta foto" first
  // bubbled the click to the parent (which opened the file picker), then the
  // camera opened only after the user closed the file picker.
  const singleAction = photoInputMode !== "file_and_camera"
    ? () => (photoInputMode === "camera" ? cameraRef : fileRef).current?.click()
    : null;
  const useCamera = photoInputMode === "camera" || photoInputMode === "file_and_camera";
  const useFile = photoInputMode === "file" || photoInputMode === "file_and_camera";
  return (
    <div className="flex flex-col gap-2" data-testid={`slot-${slot.key}`}>
      <div className="label-eyebrow">{slot.label}{slot.required ? " *" : ""}</div>
      <div
        className={`aspect-square border-2 border-dashed border-[#1E2520]/20 rounded-md flex flex-col items-center justify-center text-center p-2 bg-[#F9F8F6] hover:bg-[#F0EBE1] transition-colors relative overflow-hidden${singleAction ? " cursor-pointer" : ""}`}
        onClick={singleAction || undefined}
      >
        {preview ? (
          <>
            <img src={preview} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`slot-clear-${slot.key}`}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute top-2 right-2 bg-[#1E2520]/80 text-white rounded-full p-1.5 hover:bg-[#1E2520]"
            >
              <X size={14} />
            </button>
          </>
        ) : photoInputMode === "file_and_camera" ? (
          <>
            <ImageSquare weight="duotone" size={32} className="text-[#9C5B42] mb-2" />
            <div className="flex flex-col gap-1 w-full px-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                className="text-xs px-2 py-1.5 border border-[#9C5B42]/40 hover:bg-[#9C5B42]/10 transition-colors text-[#1E2520]"
                data-testid={`slot-file-${slot.key}`}
              >
                Carica file
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                className="text-xs px-2 py-1.5 border border-[#9C5B42]/40 hover:bg-[#9C5B42]/10 transition-colors text-[#1E2520]"
                data-testid={`slot-camera-${slot.key}`}
              >
                Scatta foto
              </button>
            </div>
          </>
        ) : photoInputMode === "camera" ? (
          <>
            <Camera weight="duotone" size={36} className="text-[#9C5B42] mb-2" />
            <p className="text-sm text-[#1E2520]">Tocca per scattare</p>
            <p className="text-xs text-[#5C635A]">apre la fotocamera</p>
          </>
        ) : (
          <>
            <ImageSquare weight="duotone" size={36} className="text-[#9C5B42] mb-2" />
            <p className="text-sm text-[#1E2520]">Tocca per allegare</p>
            <p className="text-xs text-[#5C635A]">galleria o file</p>
          </>
        )}
        {useFile && (
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`input-${slot.key}`}
            onChange={pickFile}
          />
        )}
        {useCamera && (
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid={`input-camera-${slot.key}`}
            onChange={pickFile}
          />
        )}
      </div>
    </div>
  );
};

export default function Scan() {
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [directives, setDirectives] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Tracks which Gemini key is being used so we can show contextual
  // guidance: nothing when personal key is set, a friendly hint when the
  // shared fallback is in use, and a hard banner only if neither is
  // available (shouldn't normally happen).
  const [provider, setProvider] = useState("shared"); // "personal" | "shared" | "none"
  const [photoInputMode, setPhotoInputMode] = useState("file");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) {
          setProvider(data?.active_provider || "none");
          setPhotoInputMode(data?.photo_input_mode || "file");
        }
      } catch (e) { console.warn("Scan: failed to load settings flag", e); }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPick = (slotKey, file) => {
    setFiles((s) => ({ ...s, [slotKey]: file }));
    const url = URL.createObjectURL(file);
    setPreviews((s) => ({ ...s, [slotKey]: url }));
  };
  const onClear = (slotKey) => {
    setFiles((s) => { const c = { ...s }; delete c[slotKey]; return c; });
    setPreviews((s) => { const c = { ...s }; if (c[slotKey]) URL.revokeObjectURL(c[slotKey]); delete c[slotKey]; return c; });
  };

  const submit = async () => {
    setError("");
    if (!files.fronte) { setError("La foto del fronte è obbligatoria."); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("fronte", files.fronte);
      if (files.retro) form.append("retro", files.retro);
      if (files.lato) form.append("lato", files.lato);
      if (files.lato2) form.append("lato2", files.lato2);
      if (directives) form.append("directives", directives);
      const { data } = await api.post("/artifacts/identify", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      navigate(`/artifact/${data.id}?new=1`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Errore nell'identificazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 fade-in text-center">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#F0EBE1] mb-6 animate-pulse">
          <Sparkle weight="duotone" size={42} className="text-[#9C5B42]" />
        </div>
        <h2 className="font-serif text-4xl text-[#1E2520] mb-3">Analisi in corso...</h2>
        <p className="text-[#5C635A] max-w-md mx-auto">
          L'AI sta esaminando le tue foto, confrontandole con archivi numismatici e archeologici
          e calcolando una stima di valore. Potrebbe richiedere fino a un minuto.
        </p>
        <div className="mt-10 max-w-md mx-auto space-y-3">
          <div className="h-3 scanning" />
          <div className="h-3 scanning w-5/6 mx-auto" />
          <div className="h-3 scanning w-2/3 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
      <div className="mb-8">
        <div className="label-eyebrow mb-3">Identificazione</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E2520]">
          Cattura il tuo reperto
        </h1>
        <p className="text-[#5C635A] mt-3">
          Fornisci almeno la foto del <strong>fronte</strong>. Aggiungi <em>retro</em>, <em>lato</em> e
          <em> lato 2</em> per un'identificazione più precisa.
        </p>
      </div>

      {provider === "shared" && (
        <div
          className="mb-6 p-4 rounded-sm border border-[#E5E0D8] bg-[#F9F8F6] flex items-start gap-3"
          data-testid="shared-key-tip"
        >
          <Sparkle weight="duotone" size={22} className="text-[#9C5B42] shrink-0 mt-0.5" />
          <div className="text-sm text-[#1E2520]">
            <strong>Stai usando la chiave condivisa Reperti.</strong>{" "}
            <span className="text-[#5C635A]">
              Funziona subito senza configurazioni, ma la quota è condivisa con gli altri utenti.
              Per quote più ampie e tutte tue, aggiungi una chiave personale gratuita in{" "}
              <Link to="/settings" className="text-[#9C5B42] underline">Impostazioni</Link>.
            </span>
          </div>
        </div>
      )}

      {provider === "none" && (
        <div
          className="mb-8 p-5 rounded-sm border-2 border-amber-500/50 bg-amber-50"
          data-testid="missing-key-banner"
        >
          <div className="flex items-start gap-3">
            <Warning weight="duotone" size={28} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-[#1E2520]">
              <div className="font-serif text-lg mb-1.5">Chiave Gemini mancante</div>
              <p className="mb-3">
                Per identificare i reperti con l'AI ti serve una chiave Google Gemini personale.
                È <strong>gratuita</strong> e si crea in 2 minuti su Google AI Studio.
              </p>
              <Link
                to="/settings"
                className="btn-primary inline-flex items-center gap-2 text-sm"
                data-testid="missing-key-cta"
              >
                <Key weight="duotone" size={16} /> Aggiungi la tua chiave in Impostazioni
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        {SLOTS.map((s) => (
          <PhotoSlot
            key={s.key}
            slot={s}
            file={files[s.key]}
            preview={previews[s.key]}
            onPick={(f) => onPick(s.key, f)}
            onClear={() => onClear(s.key)}
            photoInputMode={photoInputMode}
          />
        ))}
      </div>

      <div className="mb-8">
        <label className="label-eyebrow block mb-2">Suggerimenti per l'AI (opzionale)</label>
        <textarea
          data-testid="directives-input"
          className="input-base min-h-[100px]"
          placeholder="Es: trovato in Toscana, sembra una moneta romana imperiale, peso 3.2g..."
          value={directives}
          onChange={(e) => setDirectives(e.target.value)}
        />
      </div>

      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 text-red-800 px-4 py-3 rounded-sm" data-testid="scan-error">
          {error}
          {/* If the error looks like a brand-new API key still propagating on
              Google's side, hint to the user that a short wait usually fixes
              it. We match a few common Google-side error fragments. */}
          {/(api[_ ]?key[_ ]?(not[_ ]?valid|invalid|expired)|api_key_invalid|404|not[_ ]?found|models\/.+not.*found|permission.?denied)/i.test(error) && (
            <div
              className="mt-3 pt-3 border-t border-red-200 text-amber-900 bg-amber-50 -mx-4 -mb-3 px-4 py-3 rounded-b-sm flex gap-2"
              data-testid="scan-key-hint"
            >
              <span className="text-base" aria-hidden>⏱️</span>
              <div className="text-sm leading-relaxed">
                <strong>Hai appena creato o sostituito la chiave Gemini?</strong>{" "}
                Google può impiegare <strong>fino a 10 minuti</strong> per
                attivarla. Aspetta qualche minuto e ritenta — non serve
                generarne una nuova. Se l'errore persiste oltre 10 minuti,
                verifica che la chiave sia stata copiata correttamente in{" "}
                <a href="/settings" className="underline">Impostazioni</a>.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={submit}
          disabled={!files.fronte}
          className="btn-primary inline-flex items-center justify-center gap-2"
          data-testid="scan-submit"
        >
          <Camera weight="duotone" size={18} />
          Identifica reperto <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
